// Shared recipe → SQL helpers used by both the inline editor (default
// landing surface in BqDatasetPanelV2) and the modal builder
// (RecipeBuilderModal). Same recipe shape compiles to the same SQL +
// the same FilterTree, so the trigger logic created on save matches
// the BQ view's WHERE clause exactly.

import type { FilterCondition, FilterOp, FilterTree } from '@datahub/shared';

/** Build an "auto-projected" SELECT that flattens payload top-level
 *  fields into named columns. Used as the default SQL for the V3
 *  inline editor when the operator hasn't picked specific fields yet
 *  — the table shows payload structured, with envelope columns
 *  intentionally absent.
 *
 *  Conventions:
 *  - `event_id` is included as the first column so click-to-lineage
 *    still works even though it's a "system field".
 *  - `received_at` second — primary sort axis, useful chronological
 *    anchor.
 *  - All payload paths (from /events:schema) become JSON_VALUE
 *    projections with the path as the column alias (backtick-quoted
 *    to allow non-ASCII like Croatian field names).
 *  - When the schema is empty (no events yet), falls back to a
 *    minimal envelope projection so the operator sees something. */
export function buildAutoProjectedSql(opts: {
  dataset: string;
  baseView: string;
  payloadPaths: string[];
  /** Optional explicit sort: BQ-safe column name + direction. Falls
   *  back to `received_at DESC` (newest-first) when unset. */
  sortColumn?: string;
  sortDir?: 'ASC' | 'DESC';
}): string {
  const fqBase = `\`${opts.dataset}.${opts.baseView}\``;
  const orderBy = opts.sortColumn
    ? `ORDER BY \`${opts.sortColumn}\` ${opts.sortDir ?? 'DESC'}`
    : 'ORDER BY received_at DESC';
  if (opts.payloadPaths.length === 0) {
    return `SELECT event_id, received_at\nFROM ${fqBase}\n${orderBy}`;
  }
  const projections = [
    'event_id',
    'received_at',
    ...opts.payloadPaths.map(p => `JSON_VALUE(payload, '$.${p}') AS \`${p}\``),
  ].join(',\n  ');
  return `SELECT\n  ${projections}\nFROM ${fqBase}\n${orderBy}`;
}

export interface FieldPick {
  source_path: string;
  alias: string;
  cast: 'string' | 'float' | 'int' | 'bool' | 'json';
}

export interface FilterRow {
  field_path: string;
  op: 'is_present' | 'is_empty' | 'eq' | 'neq' | 'gt' | 'lt' | 'contains';
  value: string;
}

export interface RecipeSpec {
  dataset: string;
  baseView: string;          // 'events' or any other view in the dataset
  picks: FieldPick[];
  filters: FilterRow[];
  uniqueByPath: string;
  uniqueOrderBy: 'received_at_desc' | 'received_at_asc';
}

const CAST_TO_BQ_FN: Record<FieldPick['cast'], (jsonExpr: string) => string> = {
  string: (e) => `JSON_VALUE(${e})`,
  float:  (e) => `CAST(JSON_VALUE(${e}) AS FLOAT64)`,
  int:    (e) => `CAST(JSON_VALUE(${e}) AS INT64)`,
  bool:   (e) => `CAST(JSON_VALUE(${e}) AS BOOL)`,
  json:   (e) => `JSON_EXTRACT(${e})`,
};

function sqlString(s: string): string { return `'${s.replace(/'/g, "''")}'`; }
function sqlNumber(s: string): string { return Number.isFinite(Number(s)) ? String(Number(s)) : '0'; }

/** Top-level columns of the `events` BQ table that are NOT JSON.
 *  Filters on these emit bare column references rather than
 *  JSON_VALUE(payload, …). Keeps the operator-friendly field
 *  authoring ("filter on received_at") working without surfacing
 *  the BQ schema split. */
const SYSTEM_COLUMNS = new Set([
  'event_id',
  'event_key',
  'source_id',
  'received_at',
  'derivation_depth',
  'parent_event_id',
]);

/** Resolve a field path to a SQL value-expression. Three branches:
 *    - $origin.X.Y.Z  → JSON_VALUE(origin, '$.X.Y.Z')
 *    - system column  → bare reference (e.g. `received_at`)
 *    - everything else → JSON_VALUE(payload, '$.path')
 *  The expr is returned wrapped in plain JSON_VALUE form so the
 *  caller can wrap it (CAST/STRPOS/LOWER/etc) per operator.
 */
function fieldExprForSql(path: string): string {
  if (path.startsWith('$origin.')) {
    const sub = path.slice('$origin.'.length);
    return `JSON_VALUE(origin, '$.${sub}')`;
  }
  if (SYSTEM_COLUMNS.has(path)) {
    // received_at is a TIMESTAMP, others STRING — string-coerce so the
    // shared CAST(... AS FLOAT64) / STRPOS shapes work uniformly.
    return `CAST(${path} AS STRING)`;
  }
  return `JSON_VALUE(payload, '$.${path}')`;
}

/** Exported so V3 can verify SQL-builder parity in unit tests
 *  (audit finding TEST.2 — V2/V3 divergence risk). */
export function filterToSql(f: FilterRow): string | null {
  if (!f.field_path.trim()) return null;
  const path = f.field_path.trim();
  const v = f.value;
  const expr = fieldExprForSql(path);
  switch (f.op) {
    case 'is_present': return `${expr} IS NOT NULL`;
    case 'is_empty':   return `(${expr} IS NULL OR ${expr} = '')`;
    case 'eq':         return `${expr} = ${sqlString(v)}`;
    case 'neq':        return `${expr} != ${sqlString(v)}`;
    case 'gt':         return `CAST(${expr} AS FLOAT64) > ${sqlNumber(v)}`;
    case 'lt':         return `CAST(${expr} AS FLOAT64) < ${sqlNumber(v)}`;
    case 'contains':   return `STRPOS(LOWER(${expr}), LOWER(${sqlString(v)})) > 0`;
  }
}

/** SELECT-projection expression for a pick that might target a payload
 *  path, a top-level system column, or a $origin.* meta path. Pick
 *  cast is honoured for payload paths; meta paths cast off the same
 *  string-typed JSON_VALUE / column. */
function pickExprForSql(p: { source_path: string; cast: keyof typeof CAST_TO_BQ_FN }): string {
  if (p.source_path.startsWith('$origin.')) {
    const sub = p.source_path.slice('$origin.'.length);
    return CAST_TO_BQ_FN[p.cast](`origin, '$.${sub}'`);
  }
  if (SYSTEM_COLUMNS.has(p.source_path)) {
    // System columns are already top-level — no JSON_VALUE wrap. Cast
    // only matters for derivation_depth (already INT) and received_at
    // (TIMESTAMP); string is the safe default for everything else.
    return p.source_path;
  }
  return CAST_TO_BQ_FN[p.cast](`payload, '$.${p.source_path}'`);
}

export function buildRecipeSql(opts: RecipeSpec): string {
  const fqBase = `\`${opts.dataset}.${opts.baseView}\``;
  const projection = opts.picks.length === 0
    ? 'event_id, source_id, received_at, payload, origin'
    : [
        'event_id', 'source_id', 'received_at',
        ...opts.picks.map(p => `${pickExprForSql(p)} AS ${p.alias}`),
      ].join(',\n  ');

  const whereClauses = opts.filters.map(f => filterToSql(f)).filter((s): s is string => s !== null);
  const whereSql = whereClauses.length > 0 ? `WHERE ${whereClauses.join('\n  AND ')}\n` : '';

  if (opts.uniqueByPath.trim()) {
    const order = opts.uniqueOrderBy === 'received_at_asc' ? 'ASC' : 'DESC';
    const uniqExpr = fieldExprForSql(opts.uniqueByPath.trim());
    return `-- Recipe: pick fields + filter + unique-by\nSELECT * EXCEPT(_rn) FROM (\n  SELECT\n    ${projection},\n    ROW_NUMBER() OVER (\n      PARTITION BY ${uniqExpr}\n      ORDER BY received_at ${order}\n    ) AS _rn\n  FROM ${fqBase}\n  ${whereSql.trim()}\n)\nWHERE _rn = 1`;
  }

  return `-- Recipe: pick fields + filter\nSELECT\n  ${projection}\nFROM ${fqBase}\n${whereSql}`.trim();
}

const RECIPE_OP_TO_FILTER_OP: Record<FilterRow['op'], FilterOp> = {
  is_present: 'is_present',
  is_empty:   'is_empty',
  eq:         'equals',
  neq:        'not_equals',
  gt:         'gt',
  lt:         'lt',
  contains:   'contains',
};

export function filterRowsToTree(rows: FilterRow[]): FilterTree | null {
  const valid = rows.filter(r => r.field_path.trim());
  if (valid.length === 0) return null;
  const conditions: FilterCondition[] = valid.map(r => {
    const cond: FilterCondition = {
      field: r.field_path.trim(),
      operator: RECIPE_OP_TO_FILTER_OP[r.op],
    };
    if (!['is_present', 'is_empty'].includes(r.op)) {
      cond.value = r.value;
    }
    return cond;
  });
  return { combinator: 'AND', conditions };
}
